import _ from 'underscore';
import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { GridContext, ContextualDetailsList } from '@bingads-webui-grid/grid-context';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { FilterViewManager } from '@bingads-webui-campaign/reports-v4-filter-view-manager';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { SchemaI18n } from '@bingads-webui-universal/schema-i18n';
import { ApplyBrandListPanel, ManageBrandListPanel } from '@bingads-webui-campaign-react/brand-lists-components';
import { useCampaignGridDataSource } from '@bingads-webui-grid/campaign-data-source';
import { getBrandListsEdm, deleteBrandListFromProp, getViewModelBulkUpsertErrors, GENERIC_ERROR_MESSAGE } from '@bingads-webui-campaign-react/brand-lists-data';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { withSiteMap, siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { HelpLink } from '@bingads-webui-react/help-link';
import { DeleteDialog } from '../components/delete-dialog';
import { buildColumns } from './configs/columns-config';
import { getToolbarConfig } from './configs/toolbar-config';
import { getFilterConfig } from './configs/filter-config';
import { getStyles } from './styles';

const ToolbarShownItem = {
  Toolbar: 'Toolbar',
  FilterView: 'FilterView',
};

function filterItems(items, filter) {
  const type = _.get(filter, ['BrandListType', '$in', 0]);
  if (type === "Enum.BrandListType'both'") {
    return items.filter(item => item.CampaignAssociationCount > 0 && item.CampaignExclusionAssociationCount > 0);
  }
  if (type === "Enum.BrandListType'protection'") {
    return items.filter(item => item.CampaignAssociationCount > 0 && item.CampaignExclusionAssociationCount === 0);
  }
  if (type === "Enum.BrandListType'exclusion'") {
    return items.filter(item => item.CampaignAssociationCount === 0 && item.CampaignExclusionAssociationCount > 0);
  }
  if (type === "Enum.BrandListType'none'") {
    return items.filter(item => item.CampaignAssociationCount === 0 && item.CampaignExclusionAssociationCount === 0);
  }
  return items;
}

function BrandListsPageContentInner({ siteMap }) {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { state, dispatch } = useContext(GridContext);
  const classes = useDefaultStyles(getStyles);
  const { scope } = useContext(ScopeContext);
  const {
    values: {
      cid,
      aid,
      brandListId,
    },
  } = scope;

  const [toolbarShownItem, setToolbarShownItem] = useState(ToolbarShownItem.Toolbar);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showApplyBrandListPanel, setShowApplyBrandListPanel] = useState(false);
  const [applyBrandListType, setApplyBrandListType] = useState('protection');
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isBrandProtectionEnabled = !!serverContext.DynamicPermissions.BroadOnly || (!!serverContext.DynamicPermissions.PredictiveMatching && !!serverContext.DynamicPermissions.PredictiveMatchingPhase2);
  const isBrandExclusionEnabled = !!serverContext.DynamicPermissions.BrandExclusion;

  const edmProps = {
    cid: serverContext.CurrentCustomer.Id,
    aid: serverContext.CurrentAccount.Id,
  };
  const deleteBrandList = deleteBrandListFromProp(edmProps);

  const toolbarConfig = useMemo(() => getToolbarConfig({
    i18n,
    selectedItems: state.selectedItems,
    isBrandProtectionEnabled,
    isBrandExclusionEnabled,
    handleCreate: () => {
      setIsCreatePanelOpen(true);
    },
    handleDelete: () => {
      setShowDeleteDialog(true);
    },
    handleExclusion: () => {
      setApplyBrandListType('exclusion');
      setShowApplyBrandListPanel(true);
    },
    handleProtection: () => {
      setApplyBrandListType('protection');
      setShowApplyBrandListPanel(true);
    },
  }), [i18n, isBrandExclusionEnabled, isBrandProtectionEnabled, state.selectedItems]);

  const toolbarEditor = useMemo(() => getToolbarEditor({ toolbarConfig }), [toolbarConfig]);
  const filterConfig = useMemo(() => getFilterConfig({
    i18n,
    isBrandProtectionEnabled,
    isBrandExclusionEnabled,
  }), [i18n, isBrandExclusionEnabled, isBrandProtectionEnabled]);

  const reloadData = useCallback(async () => {
    dispatch({ type: 'REFRESH_GRID_DATA' });
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_ITEMS', payload: [] });
    state.selection.setAllSelected(false);
  }, [dispatch, state.selection]);

  const setItemSelect = useCallback((item) => {
    const items = state.selection.getItems();
    const key = items.findIndex(v => v.Id === item.Id);
    state.selection.setKeySelected(key, true);
    dispatch({ type: 'SET_SELECTED_ITEMS', payload: [item] });
  }, [dispatch, state.selection]);

  useCampaignGridDataSource({
    edm: getBrandListsEdm({
      customerId: serverContext.CurrentCustomer.Id,
      accountId: serverContext.CurrentAccount.Id,
    }),
    odataParams: {
      query: {
        filter: undefined,
      },
    },
    formatResponse: response => ({
      items: filterItems(response.value, state.editableFilter),
      totalCount: response['@odata.count'],
    }),
    onError: () => {
      notificationApi.showWarning(i18n.getString(GENERIC_ERROR_MESSAGE));
    },
  });

  const resetToolbarVisibility = useCallback(({ forceHideToolbar }) => {
    if (forceHideToolbar) {
      setToolbarShownItem(ToolbarShownItem.FilterView);
    } else {
      setToolbarShownItem(ToolbarShownItem.Toolbar);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteBrandList(state.selectedItems.map(v => v.Id));
    } catch (res) {
      const serverErrors = getViewModelBulkUpsertErrors(i18n, res);
      notificationApi.showWarning(serverErrors[0].message);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      clearSelection();
      dispatch({ type: 'REFRESH_GRID_DATA' });
    }
  }, [clearSelection, deleteBrandList, dispatch, i18n, state.selectedItems]);


  useEffect(() => {
    const newColumns = buildColumns({
      i18n,
      siteMap,
      scope,
      isBrandProtectionEnabled,
      isBrandExclusionEnabled,
      protectionAssociation: (item) => {
        setItemSelect(item);
        setApplyBrandListType('protection');
        setShowApplyBrandListPanel(true);
      },
      exclusionAssociation: (item) => {
        setItemSelect(item);
        setApplyBrandListType('exclusion');
        setShowApplyBrandListPanel(true);
      },
    });
    dispatch({ type: 'UPDATE_SELECTED_COLUMNS', payload: newColumns });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, isBrandExclusionEnabled, isBrandProtectionEnabled, scope, serverContext, siteMap]);


  function renderToolbarContainer() {
    return (
      <ToolbarContainer
        i18n={i18n}
        forceHideToolbar={toolbarShownItem !== ToolbarShownItem.Toolbar}
        showToolbarUnselected={state.selectedItems.length === 0}
        showToolbarSelected={state.selectedItems.length > 0}
        toolbarSelectedConfig={toolbarConfig.toolbarSelectedConfig}
        toolbarUnselectedConfig={toolbarConfig.toolbarUnselectedConfig}
        onClear={clearSelection}
        selectionCount={state.selectedItems.length}
      />
    );
  }

  function renderFilterViewManager() {
    return (
      <div>
        <FilterViewManager
          updateToolbar={toolbarEditor.update}
          resetToolbarVisibility={v => resetToolbarVisibility(v)}
          legacyI18n={i18n}
          hideSaveFilterButton
          {...filterConfig}
          schemaI18n={new SchemaI18n({ i18n })}
          editableFilter={state.editableFilter}
          setEditableFilter={(query) => {
            dispatch({ type: 'SET_EDITABLE_FILTER', payload: query });
          }}
        />
      </div>
    );
  }

  const toolbar = (
    <React.Fragment>
      {renderToolbarContainer()}
      {renderFilterViewManager()}
    </React.Fragment>
  );

  return (
    <div className={`${classes.BrandListsPage} brand-lists-page`}>
      <h2 className="title">{i18n.getString(_TL_('Brand lists'))}</h2>
      <p className="desc">
        {i18n.getString(_TL_('You can create up to 20 brand lists. '))}
        {isBrandProtectionEnabled && isBrandExclusionEnabled && i18n.getString(_TL_('There are two types of brand lists. '))}
        {isBrandExclusionEnabled && i18n.getString(_TL_('Brand exclusion lists are only available for Performance Max campaigns. '))}
        {isBrandProtectionEnabled && i18n.getString(_TL_('Brand protection lists are available for Search campaigns that only use broad match for keywords. '))}
        <HelpLink query="help:app54850">{i18n.getString(_TL_('Learn more'))}</HelpLink>
      </p>
      <ContextualDetailsList
        setKey="items"
        stickyHeader
        classes={classes}
        toolbar={toolbar}
        placeholder={i18n.getString(_TL_('No brand lists found.'))}
        showPagination={false}
      />
      <DeleteDialog
        title={i18n.getString(_TL_('Delete brand list(s)'))}
        description={i18n.getString(_TL_('Are you sure you want to delete the list(s)?'))}
        open={showDeleteDialog}
        onConfirm={handleDelete}
        deleting={deleting}
        onDismiss={() => {
          setShowDeleteDialog(false);
          clearSelection();
        }}
      />
      <ApplyBrandListPanel
        isOpen={showApplyBrandListPanel}
        type={applyBrandListType}
        brandLists={state.selectedItems}
        onSave={() => {
          dispatch({ type: 'REFRESH_GRID_DATA' });
          setShowApplyBrandListPanel(false);
          clearSelection();
        }}
        onDismiss={() => {
          setShowApplyBrandListPanel(false);
          clearSelection();
        }}
      />
      <ManageBrandListPanel
        cid={cid}
        aid={aid}
        brandListId={brandListId}
        className="create-brand-list-panel"
        isOpen={isCreatePanelOpen}
        onDismiss={() => setIsCreatePanelOpen(false)}
        onSaveCallback={reloadData}
      />
    </div>
  );
}

BrandListsPageContentInner.propTypes = {
  siteMap: siteMapPropType.isRequired,
};

export const BrandListsPageContent = withSiteMap(BrandListsPageContentInner);
